// import React, { useState } from "react";
import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { Box, Typography, useTheme, Paper, Dialog, DialogContent } from "@mui/material";
import SidebarLayout from "../../../shared/sections/sidebar/SidebarLayout";
import TitleSection from "../../../shared/sections/TitleSection";
import LeftNavigation from "../../../shared/sections/NavigationSection";
import InfoNotification from "../../../shared/modules/InfoNotification";
import QuickCostButton from "../../../shared/modules/QuickCostButton";
import CircularLoading from "../../../shared/modules/CircularLoading";
import ProgressiveAnalysisTracker from "../casting/ProgressiveAnalysisTracker";
import FormulationTable from "../../../shared/modules/FormulationTable";
import ResultsSection from "../../../shared/sections/ResultsSection";
import { PlasticsQuestions } from "./questions";
import { PlasticsPlayBook } from "./playbook";
import DescriptionIcon from "@mui/icons-material/Description";
import ScienceIcon from "@mui/icons-material/Science";
import TuneIcon from "@mui/icons-material/Tune";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { theme } from "../../../design/theme";
import { apiFetch } from "../../../api/apiClient";
import { useAuth } from "../../../context/AuthContext";
import { overheadCostPerPart, round4 } from "./plasticsCostMath.js";
import { costBreakdownFromSql, overheadRatePercent } from "./plasticsOverhead.js";

const { perlColors, perlTypography } = theme.custom;
const DEFAULT_BREAKDOWN_ORDER = ["material", "process",  "ohp"];
// const normalizeKey = (key = "") => key.toLowerCase().replace(/[\s_-]+/g, "");
const SKIP_BREAKDOWN_KEYS = new Set(["currency", "total_should_cost", "total", "logistics"]);
// const SKIP_BREAKDOWN_KEYS = new Set(["currency", "total_should_cost", "logistics","total"].map(normalizeKey));
// const ALLOWED_BREAKDOWN_KEYS = new Set(DEFAULT_BREAKDOWN_ORDER.map(normalizeKey));
const BREAKDOWN_LABELS = {
  material: "Material",
  process: "Process",
  ohp: "Overhead & Profits",
};
 
const toLabel = (key) =>
  (key || "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
 
const buildCostRows = (breakdown = {}) => {
  const entries = Object.entries(breakdown).filter(([key, value]) => {
    // const normKey = normalizeKey(key);
    // if (SKIP_BREAKDOWN_KEYS.has(normKey)) return false;
    if (SKIP_BREAKDOWN_KEYS.has(key)) return false;
    // if (ALLOWED_BREAKDOWN_KEYS.size > 0 && !ALLOWED_BREAKDOWN_KEYS.has(normKey)) return false;
    return Number.isFinite(Number(value));
  });
 
  entries.sort((a, b) => {
    // const ai = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => normalizeKey(k) === normalizeKey(a[0]));
    // const bi = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => normalizeKey(k) === normalizeKey(b[0]));
    const ai = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => k === a[0]);
    const bi = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => k === b[0]);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return a[0].localeCompare(b[0]);
  });
 
  return entries.map(([key, value]) => {
    // const normKey = normalizeKey(key);
    // const label = BREAKDOWN_LABELS[key] || BREAKDOWN_LABELS[normKey] || toLabel(key);
    const label = BREAKDOWN_LABELS[key] || toLabel(key);
    return {
      key,
      label,
      value: Number(value) || 0,
    };
  });
};

// Handling assumption section locally and rendering the component here
function renderAssumptions({
  assumptions=[] ,
  productData,
  formulaResult,
}){

  return (
  <Box>
    {assumptions && assumptions.length > 0 && (
              <Paper
                sx={{
                  p: 3,
                  bgcolor: perlColors.surface,
                  border: `1px solid ${perlColors.border}`,
                  borderRadius: 2,
                  mt: 3,
                }}
              >
                {(() => {
                  const bottomSectionKeywords = [
                    "Machine tonnage:",
                    "Tonnage:",
                    "Cycle time:",
                    "Cycle Time(sec):",
                    "Cavities:",
                    "Runner type:",
                    "Machine rate:",
                    "OHP rate:",
                    "No. of operations:",
                    "Material price:",
                    "Material Price:",
                    "Material price refresh",
                    "Labour cost:",
                    "Labour rate:",
                    "Labour Rate:",
                    "Glue flap:",
                    "Board cost per box:",
                    "Ink cost per box:",
                    "Material spec:",
                    "Ink cost per_box:",
                    "Total conversion cost:",
                    "OHP:",
                  ];

                  // Case-insensitive matching for keywords
                  const topAssumptions = assumptions.filter(
                    (assumption) => {
                      // Ensure assumption is a string
                      const assumptionStr = typeof assumption === 'string' ? assumption : String(assumption || '');
                      return !bottomSectionKeywords.some((keyword) =>
                        assumptionStr.toLowerCase().startsWith(keyword.toLowerCase())
                      );
                    }
                  );
                  let bottomAssumptions = assumptions.filter((assumption) => {
                    // Ensure assumption is a string
                    const assumptionStr = typeof assumption === 'string' ? assumption : String(assumption || '');
                    return bottomSectionKeywords.some((keyword) =>
                      assumptionStr.toLowerCase().startsWith(keyword.toLowerCase())
                    );
                  });

                  // Map values from formulaResult if available
                  if (formulaResult) {
                    // Extract required_ton from formulaResult.MachineTonnage.required_ton
                    const requiredTonnage = formulaResult.MachineTonnage?.required_ton;
                    if (requiredTonnage !== undefined && requiredTonnage !== null) {
                      // Remove existing "Machine tonnage:" or "Tonnage:" entries and add new one
                      bottomAssumptions = bottomAssumptions.filter((assumption) => {
                        const assumptionStr = typeof assumption === 'string' ? assumption : String(assumption || '');
                        return !assumptionStr.toLowerCase().startsWith("machine tonnage:") && 
                               !assumptionStr.toLowerCase().startsWith("tonnage:");
                      });
                      bottomAssumptions.push(`Machine tonnage: ${parseFloat(requiredTonnage).toFixed(2)} tons`);
                    }

                    // Extract total_cycle_s from formulaResult.CycleTime.total_cycle_s
                    const totalCycleTime = formulaResult.CycleTime?.total_cycle_s;
                    if (totalCycleTime !== undefined && totalCycleTime !== null) {
                      // Remove existing "Cycle time:" or "Cycle Time(sec):" entries and add new one
                      bottomAssumptions = bottomAssumptions.filter((assumption) => {
                        const assumptionStr = typeof assumption === 'string' ? assumption : String(assumption || '');
                        return !assumptionStr.toLowerCase().startsWith("cycle time") && 
                               !assumptionStr.toLowerCase().startsWith("cycle time(sec):");
                      });
                      bottomAssumptions.push(`Cycle Time(sec): ${parseFloat(totalCycleTime).toFixed(2)}`);
                    }
                  }

                  // Build product information from productData if available
                  const buildProductInfo = () => {
                    if (!productData) {
                      // Fallback to topAssumptions if productData not available
                      return topAssumptions.map((assumption) => assumption);
                    }

                    const productInfo = [];

                    // Product Name
                    if (productData.productName) {
                      productInfo.push(`Product Name: ${productData.productName}`);
                    }

                    // Material
                    if (productData.materialSelection) {
                      productInfo.push(`Material: ${productData.materialSelection}`);
                    }

                    // Batch Size
                    if (productData.batchSize) {
                      productInfo.push(`Batch Size: ${productData.batchSize}`);
                    }

                    // Part Dimensions
                    const dims = productData.partWeightDimensions;
                    if (dims) {
                      const dimParts = [];
                      if (dims.partLength) dimParts.push(`L: ${dims.partLength}mm`);
                      if (dims.partWidth) dimParts.push(`W: ${dims.partWidth}mm`);
                      if (dims.partHeight) dimParts.push(`H: ${dims.partHeight}mm`);
                      if (dims.finishWeight) dimParts.push(`Weight: ${dims.finishWeight}g`);
                      if (dimParts.length > 0) {
                        productInfo.push(`Part Dimensions: ${dimParts.join(", ")}`);
                      }
                    }

                    // Part Details (Plastics-specific fields)
                    const details = productData.partDetails;
                    if (details) {
                      const detailParts = [];
                      // Plastics-specific fields
                      if (details.noOfCavities !== undefined && details.noOfCavities !== null) {
                        detailParts.push(`Specified cavities: ${details.noOfCavities}`);
                      }
                      if (details.regrindPct !== undefined && details.regrindPct !== null) {
                        detailParts.push(`Regrind: ${details.regrindPct}%`);
                      }
                      if (details.runnerType) {
                        detailParts.push(`Runner Type: ${details.runnerType}`);
                      }
                      // Use wallThickness if available, otherwise avgWallThickness
                      const wallThickness = details.wallThickness !== undefined && details.wallThickness !== null
                        ? details.wallThickness
                        : (details.avgWallThickness !== undefined && details.avgWallThickness !== null
                            ? details.avgWallThickness
                            : null);
                      if (wallThickness !== null) {
                        detailParts.push(`Wall Thickness: ${wallThickness}mm`);
                      }
                      if (detailParts.length > 0) {
                        productInfo.push(`Part Details: ${detailParts.join(", ")}`);
                      }
                    }

                    // Manufacturing Country
                    if (productData.manufacturingCountry) {
                      productInfo.push(
                        `Manufacturing Country: ${productData.manufacturingCountry}`
                      );
                    }

                    // Don't add remainingAssumptions - only show actual product data
                    // This prevents raw values like "50", "20", "USA" from appearing
                    return productInfo;
                  };

                  const productInfoItems = buildProductInfo();

                  return (
                    <Box sx={{ display: "flex", gap: 3 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            mb: 2,
                            fontSize: "14px",
                            fontWeight: 600,
                            lineHeight: "18px",
                            fontFamily: perlTypography.family,
                            letterSpacing: "0.3px",
                            color: perlColors.textPrimary,
                          }}
                        >
                          Product Information
                        </Typography>
                        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                          {productInfoItems.map((item, idx) => {
                            // Ensure item is a string (handle objects, arrays, etc.)
                            const itemStr = typeof item === 'string' 
                              ? item 
                              : (typeof item === 'object' && item !== null 
                                  ? (item.value || item.name || item.reason || JSON.stringify(item))
                                  : String(item || ''));
                            return (
                              <Typography
                                key={idx}
                                component="li"
                                variant="body2"
                                sx={{
                                  mb: 1,
                                  fontSize: "14px",
                                  fontFamily: perlTypography.family,
                                  color: perlColors.textSecondary,
                                  lineHeight: "20px",
                                  letterSpacing: "0.3px",
                                }}
                              >
                                {itemStr}
                              </Typography>
                            );
                          })}
                        </Box>
                      </Box>

                      {bottomAssumptions.length > 0 && (
                        <Box
                          sx={{
                            width: "1px",
                            bgcolor: perlColors.border || "rgba(255, 255, 255, 0.12)",
                            alignSelf: "stretch",
                          }}
                        />
                      )}

                      {bottomAssumptions.length > 0 && (
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              mb: 2,
                              fontSize: "14px",
                              fontWeight: 600,
                              lineHeight: "18px",
                              fontFamily: perlTypography.family,
                              letterSpacing: "0.3px",
                              color: perlColors.textPrimary,
                            }}
                          >
                            Effective Process Assumptions
                          </Typography>
                          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                            {bottomAssumptions.map((assumption, idx) => {
                              // Ensure assumption is a string (handle objects, arrays, etc.)
                              const assumptionStr = typeof assumption === 'string' 
                                ? assumption 
                                : (typeof assumption === 'object' && assumption !== null 
                                    ? (assumption.value || assumption.name || assumption.reason || JSON.stringify(assumption))
                                    : String(assumption || ''));
                              return (
                                <Typography
                                  key={idx}
                                  component="li"
                                  variant="body2"
                                  sx={{
                                    mb: 1,
                                    fontSize: "14px",
                                    fontFamily: perlTypography.family,
                                    color: perlColors.textSecondary,
                                    lineHeight: "20px",
                                    letterSpacing: "0.3px",
                                  }}
                                >
                                  {assumptionStr}
                                </Typography>
                              );
                            })}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  );
                })()}
              </Paper>
            )}

  </Box>
)
}


export default function PlasticsPage() {
  const { user } = useAuth();
  const theme = useTheme();
  const perlColors = theme.custom?.perlColors || {
    background: "#191A1B",
    textPrimary: "#F1F2F3",
  };
  const perlSpacing = theme.custom?.perlSpacing || {};

  const [activeSection, setActiveSection] = useState("product");
  const [sectionStarted, setSectionStarted] = useState({
    product: false,
    formulation: false,
    playbook: false,
    results: false,
  });

  const [productData, setProductData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [recipeData, setRecipeData] = useState([]);
  const [playBookData, setPlayBookData] = useState(null);
  const [costData, setCostData] = useState(null);
  const [riskData, setRiskData] = useState(null);
  const [substituteData, setSubstituteData] = useState(null);
  const [productImageUrl, setProductImageUrl] = useState(null);
  const [productImageError, setProductImageError] = useState("");
  const imageQuoteSnapshotRef = useRef(null);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [keyInsightsData, setKeyInsightsData] = useState(null);
  const [demoMode, setDemoMode] = useState(false);

  // Progressive analysis tracker state
  const [showProgressTracker, setShowProgressTracker] = useState(false);
  const [progressSteps, setProgressSteps] = useState([]);
  const [currentProgressStep, setCurrentProgressStep] = useState(0);
  const [overallProgress, setOverallProgress] = useState(0);

  // Debug: Log keyInsightsData changes
  useEffect(() => {
    console.log("🔍 keyInsightsData changed:", keyInsightsData);
    if (keyInsightsData) {
      console.log("   - Has cost_savings:", Array.isArray(keyInsightsData.cost_savings), "length:", keyInsightsData.cost_savings?.length);
      console.log("   - Has strategic:", Array.isArray(keyInsightsData.strategic), "length:", keyInsightsData.strategic?.length);
    }
  }, [keyInsightsData]);

  // Functional groups for formulation
  const functionalGroups = [
    "Bulk ingredient",
    "Sweetners",
    "Oil and fats",
    "Active ingredient",
    "Leavening agents",
    "Flavour enhancers",
    "Flavours",
    "Colours",
    "Preservatives",
    "Emulsifiers",
    "Stabilizers",
    "Thickeners",
    "Acidulants",
    "Vitamins and minerals",
    "Other",
  ];
  const handleStartSection = () => {
    setSectionStarted((prev) => ({ ...prev, [activeSection]: true }));
  };

  const lastProduct = useRef(null);
  const imageRequestGenerationRef = useRef(0);
  useEffect(() => () => {
    imageRequestGenerationRef.current += 1;
  }, []);
  const handleProductComplete = useCallback((data) => {
    const fingerprint = JSON.stringify(data);
    if (fingerprint === lastProduct.current) return;
    lastProduct.current = fingerprint;
    imageRequestGenerationRef.current += 1;
    imageQuoteSnapshotRef.current = null;
    setProductImageError("");
    setProductImageUrl(null);
    setGeneratingImage(false);
    setProductData(data);
    // Effective values from a previous part must not become overrides for a new part.
    setPlayBookData(null);
    setCostData(null);
    setKeyInsightsData(null);
    setRiskData(null);
    setSubstituteData(null);
  }, []);

  const handleDemoModeChange = (isDemoMode) => {
    setDemoMode(isDemoMode);
  };

  // Helper function to recalculate OHP cost when OHP Rate changes
  const recalculateOHPCost = (newOhpRate, costData, updatedCostParams) => {
    if (!costData || !costData.breakdown || !costData._originalResult) {
      return costData;
    }

    // The UI contract is an explicit percent. Currency amounts are never parsed
    // as rates, and values at or below 1 retain their percent units.
    const material = Number(costData.breakdown.material || 0);
    const process = Number(costData.breakdown.process || 0);
    const ohp = overheadCostPerPart({ materialCost: material, processCost: process, ratePercent: newOhpRate });
    const targetOhpRate = Number(newOhpRate);
    const total = round4(material + process + ohp);

    // Update assumptions array with updated PlayBook values
    const resultData = costData._originalResult;
    const assumptionsArray = [];
    
    // Part Dimensions - from product data
    const dimensionData = productData?.partWeightDimensions || {};
    if (dimensionData.partLength && dimensionData.partWidth && dimensionData.partHeight && dimensionData.finishWeight) {
      assumptionsArray.push(
        `Part dimensions: L: ${dimensionData.partLength}mm, W: ${dimensionData.partWidth}mm, H: ${dimensionData.partHeight}mm, Weight: ${dimensionData.finishWeight}g`
      );
    }
    
    // Machine Tonnage
    if (updatedCostParams.machineTonnage) {
      assumptionsArray.push(`Machine tonnage: ${updatedCostParams.machineTonnage}`);
    } else if (resultData.Tonnage || resultData.cTonnage) {
      assumptionsArray.push(`Machine tonnage: ${resultData.Tonnage || resultData.cTonnage}`);
    }
    
    // Cavities
    if (updatedCostParams.cavities) {
      assumptionsArray.push(`Cavities: ${updatedCostParams.cavities}`);
    } else if (resultData.Cavities || resultData.cCavities) {
      assumptionsArray.push(`Cavities: ${resultData.Cavities || resultData.cCavities}`);
    }
    
    // Runner Type
    if (updatedCostParams.runnerType && updatedCostParams.runnerType !== "N/A") {
      assumptionsArray.push(`Runner type: ${updatedCostParams.runnerType}`);
    } else if (resultData.RunnerType) {
      assumptionsArray.push(`Runner type: ${resultData.RunnerType}`);
    }
    
    // Cycle Time
    if (updatedCostParams.cycleTime) {
      assumptionsArray.push(`Cycle time: ${updatedCostParams.cycleTime}s`);
    } else if (resultData.CycleTime) {
      assumptionsArray.push(`Cycle time: ${resultData.CycleTime}s`);
    }
    
    // Machine Rate - always from stored procedure
    if (resultData.MHR) {
      const machineRate = typeof resultData.MHR === 'number' ? resultData.MHR : parseFloat(resultData.MHR);
      if (!isNaN(machineRate)) {
        assumptionsArray.push(`Machine rate: $${machineRate.toFixed(4)}/hour`);
      }
    }
    
    // Material Price - always from stored procedure
    if (resultData.RMPrice) {
      const materialPrice = typeof resultData.RMPrice === 'number' ? resultData.RMPrice : parseFloat(resultData.RMPrice);
      if (!isNaN(materialPrice)) {
        assumptionsArray.push(`Material price: $${materialPrice.toFixed(4)}/kg`);
      }
    }
    
    // Labour Cost - always from stored procedure
    if (resultData.LaborCost || resultData.LabourCost || resultData.laborCost || resultData.labourCost) {
      const labourCost = typeof (resultData.LaborCost || resultData.LabourCost || resultData.laborCost || resultData.labourCost) === 'number' 
        ? (resultData.LaborCost || resultData.LabourCost || resultData.laborCost || resultData.labourCost)
        : parseFloat(resultData.LaborCost || resultData.LabourCost || resultData.laborCost || resultData.labourCost);
      if (!isNaN(labourCost)) {
        assumptionsArray.push(`Labour rate: $${labourCost.toFixed(4)}/hour`);
      }
    }
    
    // OHP Rate - use updated value
    if (newOhpRate !== null && newOhpRate !== undefined) {
      const ohpRateValue = typeof newOhpRate === 'number' ? newOhpRate : parseFloat(newOhpRate);
      if (!isNaN(ohpRateValue)) {
        assumptionsArray.push(`OHP rate: ${ohpRateValue.toFixed(2)}%`);
      }
    }

    // Update cost data with new OHP, total, and assumptions
    return {
      ...costData,
      breakdown: {
        ...costData.breakdown,
        ohp: ohp,
        total_should_cost: total,
      },
      summary: costData.summary.replace(
        /\$[\d.]+ per unit/,
        `$${total} per unit`
      ),
      assumptions:
        assumptionsArray.length > 0 ? assumptionsArray : costData.assumptions,
      meta: {
        ...costData.meta,
        currentOhpRate: targetOhpRate,
      },
    };
  };

  const handlePlayBookChange = (field, value) => {
    // If updating costParams, merge it properly
    if (field === "costParams") {
      const previous = playBookData?.costParams || {};
      const overrides = { ...previous._overrides, ...value._overrides };
      let manufacturingChanged = false;
      for (const field of ["cavities", "cycleTime", "machineTonnage", "runnerType", "ohpRate"]) {
        const intentChanged = value._overrides?.[field] !== undefined &&
          value._overrides[field] !== previous._overrides?.[field];
        if (value[field] !== previous[field] || intentChanged) {
          if (field !== "ohpRate") manufacturingChanged = true;
          overrides[field] = field === "ohpRate" ||
            (field === "runnerType" ? value[field] !== "N/A" : Number(value[field]) > 0);
        }
      }
      const params = { ...value, _overrides: overrides };
      if (!overrides.machineTonnage) params.matPrice = 0;
      setPlayBookData({ ...playBookData, costParams: params });
      setKeyInsightsData(null);
      if (manufacturingChanged) {
        // The previous quote describes different manufacturing assumptions.
        imageRequestGenerationRef.current += 1;
        imageQuoteSnapshotRef.current = null;
        setProductImageError("");
        setProductImageUrl(null);
        setGeneratingImage(false);
        setCostData(null);
        setRiskData(null);
        setSubstituteData(null);
      } else if (costData && costData._source === "stored_procedure" &&
          value.ohpRate !== previous.ohpRate && overrides.ohpRate) {
        setCostData(recalculateOHPCost(value.ohpRate, costData, params));
      }
    } else {
    setPlayBookData({ ...playBookData, [field]: value });
    }
  };

  // Helper function to analyze cost with explicit data
  const analyzeCostWithData = async (recipe, playbook, product) => {
    try {
      console.log("🚀 Analyzing cost, risk, and substitutes with:", { recipe, playbook, product });

      const payload = {
        recipe: recipe,
        playBookData: playbook,
        productData: product,
      };

      // Call all three APIs in parallel
      const [costResponse, riskResponse, substituteResponse] = await Promise.all([
        apiFetch("/api/fnb/analyze-cost", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }),
        apiFetch("/api/fnb/analyze-risk", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }),
        apiFetch("/api/fnb/find-substitutes", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }),
      ]);

      // Check responses
      if (!costResponse.ok) {
        const errorData = await costResponse
          .json()
          .catch(() => ({ error: "Cost analysis failed" }));
        throw new Error(errorData.error || "Failed to analyze cost");
      }

      // Parse all responses (non-blocking for risk and substitute)
      const costResult = await costResponse.json();
      console.log("✅ Cost analysis complete:", costResult);
      setCostData(costResult);

      // Handle risk analysis
      if (riskResponse.ok) {
        const riskResult = await riskResponse.json();
        console.log("✅ Risk analysis complete:", riskResult);
        setRiskData(riskResult);
      } else {
        console.warn("⚠️ Risk analysis failed, continuing without risk data");
        setRiskData(null);
      }

      // Handle substitute analysis
      if (substituteResponse.ok) {
        const substituteResult = await substituteResponse.json();
        console.log("✅ Substitute analysis complete:", substituteResult);
        setSubstituteData(substituteResult);
      } else {
        console.warn("⚠️ Substitute analysis failed, continuing without substitute data");
        setSubstituteData(null);
      }

      // Generate product image with the playbook data
      await generateProductImageWithData(product, playbook);

      // Navigate to results section and mark as started
      setActiveSection("results");
      setSectionStarted((prev) => ({ ...prev, results: true }));

      return costResult;
    } catch (error) {
      console.error("Analysis failed:", error);
      const message = error?.message ||
        (typeof error?.error === "string" ? error.error : error?.error?.message) ||
        "The costing request failed. Please retry.";
      alert(`Failed to complete analysis: ${message}`);
      throw error;
    }
  };

  const handleAnalyzeCost = async () => {
    if (!recipeData || !playBookData || !productData) return;

    setLoadingMessage("Running cost analysis...");
    setIsProcessing(true);
    try {
      await analyzeCostWithData(recipeData, playBookData, productData);
    } finally {
      setLoadingMessage("");
      setIsProcessing(false);
    }
  };

  // Fetch risk and substitute analysis for Analysis Report tabs.
  const fetchRiskAndSubstituteData = async (product, playbook) => {
    if (!product) {
      setRiskData(null);
      setSubstituteData(null);
      return;
    }

    const payload = {
      towerName: "Mechanical",
      costModel: costData?.breakdown || {},
      form: {
        productData: product || {},
        playBookData: playbook || {},
        recipe: recipeData || [],
      },
    };

    const fallbackRecipe = Array.isArray(recipeData) && recipeData.length > 0
      ? recipeData
      : [
          {
            ingredient: product?.materialSelection || "Base Resin",
            percentage: 100,
            functional_group: "Base",
          },
        ];

    const fnbPayload = {
      recipe: fallbackRecipe,
      playBookData: playbook || {},
      productData: product || {},
    };

    const buildFallbackRisk = () => ({
      summary: `Risk snapshot for ${product?.productName || "plastic part"} based on available costing inputs.`,
      top_concerns: [
        "Raw material price volatility can affect unit economics.",
        "Cycle time drift can increase process cost.",
        "Machine utilization and downtime may impact output consistency.",
      ],
      risk_items: [
        {
          title: "Material Price Volatility",
          severity: 6,
          probability: 6,
          detail: "Track resin index and lock supplier rates where possible.",
        },
        {
          title: "Cycle Time Variability",
          severity: 5,
          probability: 5,
          detail: "Validate mold/process window and monitor actual cycle data.",
        },
      ],
      assumptions: "Generated from plastics product and playbook inputs (fallback mode).",
    });

    const buildFallbackSubstitute = () => {
      const details = product?.partDetails || {};
      const params = playbook?.costParams || {};
      const material = product?.materialSelection || "Current resin grade";
      const runnerType = params.runnerType || details.runnerType || "Current runner setup";
      const cavities = params.cavities ?? details.noOfCavities ?? "Current cavities";
      const cycleTime = params.cycleTime || "Current cycle time";
      const machineTonnage = params.machineTonnage || details.machineTonnage || "Current tonnage";

      return {
        summary: `Plastics molding optimization recommendations generated for ${product?.productName || "the current part"} using selected material and process inputs.`,
        substitutes: [
          {
            current_item: `Material: ${material}`,
            proposed_item: "Validate a cost-optimized equivalent resin grade",
            estimated_cost_delta_per_unit: "Potential material cost reduction",
            impact_on_quality: "Requires validation",
            feasibility_score: 6,
            timeline_weeks: 4,
            risk_note: "Check shrinkage, strength, and thermal behavior before release.",
            validation_steps: ["Material datasheet comparison", "Mold trial run", "Part qualification"],
          },
          {
            current_item: `Runner setup: ${runnerType}`,
            proposed_item: "Evaluate runner/gate optimization for lower scrap and cycle loss",
            estimated_cost_delta_per_unit: "Potential process cost reduction",
            impact_on_quality: "Low to medium",
            feasibility_score: 7,
            timeline_weeks: 3,
            risk_note: "Validate fill balance and cosmetic quality.",
            validation_steps: ["Moldflow review", "Pilot run", "Scrap trend check"],
          },
          {
            current_item: `Cycle time: ${cycleTime}, cavities: ${cavities}, tonnage: ${machineTonnage}`,
            proposed_item: "Tune cycle and clamp settings to improve throughput",
            estimated_cost_delta_per_unit: "Potential conversion cost reduction",
            impact_on_quality: "Process-dependent",
            feasibility_score: 7,
            timeline_weeks: 2,
            risk_note: "Monitor warpage and dimensional capability during tuning.",
            validation_steps: ["DOE on cycle settings", "Capability study", "Standardize process window"],
          },
        ],
        prioritization: [
          "Process tuning (cycle/clamp) for immediate savings",
          "Runner/gate optimization to reduce scrap",
          "Material grade optimization after process stabilization",
        ],
        assumptions: "Generated from plastics product and playbook inputs (fallback mode).",
      };
    };

    try {
      let [riskResponse, substituteResponse] = await Promise.all([
        apiFetch("/api/ai/costing-v4/risk", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }),
        apiFetch("/api/ai/costing-v4/substitute", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }),
      ]);

      // Fallback to FNB endpoints if costing-v4 fails.
      if (!riskResponse.ok || !substituteResponse.ok) {
        console.warn("costing-v4 risk/substitute failed, falling back to /api/fnb endpoints");
        [riskResponse, substituteResponse] = await Promise.all([
          apiFetch("/api/fnb/analyze-risk", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(fnbPayload),
          }),
          apiFetch("/api/fnb/find-substitutes", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(fnbPayload),
          }),
        ]);
      }

      if (riskResponse.ok) {
        const riskResult = await riskResponse.json();
        setRiskData(riskResult);
      } else {
        const riskErr = await riskResponse.json().catch(() => ({}));
        console.warn("Risk analysis failed:", riskErr);
        setRiskData(buildFallbackRisk());
      }

      if (substituteResponse.ok) {
        const substituteResult = await substituteResponse.json();
        setSubstituteData(substituteResult);
      } else {
        const substituteErr = await substituteResponse.json().catch(() => ({}));
        console.warn("Substitute analysis failed:", substituteErr);
        setSubstituteData(buildFallbackSubstitute());
      }
    } catch (error) {
      console.error("Risk/Substitute analysis failed:", error);
      setRiskData(buildFallbackRisk());
      setSubstituteData(buildFallbackSubstitute());
    }
  };

  const generateProductImageWithData = async (product, playbook) => {
    if (!product) return;

    const requestGeneration = ++imageRequestGenerationRef.current;

    imageQuoteSnapshotRef.current = JSON.parse(JSON.stringify({ product, playbook }));
    setProductImageError("");
    setProductImageUrl(null);
    setGeneratingImage(true);

    try {
      const imagePayload = {
        productName: product.productName,
        materialSelection:
          product.materialSelection?.material ||
          product.materialSelection?.name ||
          product.materialSelection ||
          "",
        companyName: "Kearney",
        brandName: "PERLab",
        commodity: "Plastics Molding",
        primaryPackaging: playbook?.primary_packaging || "",
        skuWt: product.skuWeight || "",
        skuWtUnit: product.skuWeightUnit || "g",
        marketPositioning: product.marketPositioning || "",
        style: "professional",
      };

      console.log("🖼️ Generating product image:", imagePayload);

      // const response = await apiFetch("/api/ai/generate-product-image", {
      const response = await apiFetch("/api/mech/plastics/generate-image", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(imagePayload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(typeof errorData.error === "string" ? errorData.error : "Failed to generate image");
      }

      const data = await response.json();
      if (requestGeneration !== imageRequestGenerationRef.current) return;
      const imageUrl = data.image_url || data.imageUrl;
      if (typeof imageUrl !== "string" ||
          !(/^(?:data:image\/(?:png|jpeg|webp);base64,[A-Za-z0-9+/]+={0,2}$|https:\/\/)/.test(imageUrl))) {
        throw new Error("Image service returned no usable product image.");
      }
      setProductImageUrl(imageUrl);
    } catch (error) {
      if (requestGeneration !== imageRequestGenerationRef.current) return;
      console.error("Product image generation failed:", error);
      setProductImageError(error?.status === 401 ? error.message
        : "The product image could not be generated. The calculated cost is unchanged.");
    } finally {
      if (requestGeneration === imageRequestGenerationRef.current) setGeneratingImage(false);
    }
  };

  const generateProductImage = () => {
    const snapshot = imageQuoteSnapshotRef.current;
    if (!snapshot || !costData) return;
    return generateProductImageWithData(snapshot.product, snapshot.playbook);
  };

  const fetchKeyInsights = async (product, playbook) => {
    if (!product) {
      console.log("⚠️ fetchKeyInsights: No product data provided");
      return;
    }
    try {
      console.log("🔍 Fetching key insights for plastics...");
      const response = await apiFetch("/api/mech/key-insights", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          productData: product,
          playBookData: playbook || {},
        }),
      });
      
      console.log("📥 Key insights response status:", response.status, response.statusText);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ Key insights API error:", errorText);
        throw new Error(errorText || "Failed to generate key insights");
      }
      
      const insights = await response.json();
      console.log("📊 Raw key insights response:", insights);
      
      // Normalize the response to ensure it has the expected structure
      const normalizedInsights = {
        summary: insights.summary || "",
        cost_savings: Array.isArray(insights.cost_savings) 
          ? insights.cost_savings 
          : (insights.cost_savings ? [insights.cost_savings] : []),
        strategic: Array.isArray(insights.strategic) 
          ? insights.strategic 
          : (insights.strategic ? [insights.strategic] : []),
      };
      
      console.log("✅ Normalized key insights:", normalizedInsights);
      console.log("   - Cost savings count:", normalizedInsights.cost_savings.length);
      console.log("   - Strategic count:", normalizedInsights.strategic.length);
      
      setKeyInsightsData(normalizedInsights);
    } catch (error) {
      console.error("❌ Failed to fetch key insights:", error);
      // Set empty structure on error to prevent undefined issues
      setKeyInsightsData({
        summary: "",
        cost_savings: [],
        strategic: [],
      });
    }
  };

  // Call stored procedure QC_SP_Plastics
  const callStoredProcedure = async (productData, playBookData) => {
    try {
      const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim();
      const userIdentifier =
        user?.email || user?.username || fullName || "User";

      // Extract dimension data from productData
      const dimensionData = productData.partWeightDimensions || {};
      
      // Extract details data from productData
      const detailsData = productData.partDetails || {};
      
      // Get Batch size from productData
      const batchSizeValue = productData.batchSize || "";
      let batchSize = 0;
      if (batchSizeValue) {
        if (typeof batchSizeValue === 'number') {
          batchSize = batchSizeValue;
        } else if (typeof batchSizeValue === 'string') {
          const parsed = parseFloat(batchSizeValue);
          if (!isNaN(parsed)) {
            batchSize = parsed;
          }
        }
      }

      // Get costParams from playBookData
      const costParams = playBookData?.costParams || {};

      // Prepare stored procedure parameters
      const spParams = {
        ID: 0, // Default 0
        PartName: productData.productName || "",
        MfrCountry: productData.manufacturingCountry || "",
        BatchSize: batchSize,
        MaterialName: productData.materialSelection || "",
        PartLength: dimensionData.partLength || "",
        PartWidth: dimensionData.partWidth || "",
        PartHeight: dimensionData.partHeight || "",
        PartWeight: dimensionData.finishWeight || "",
        AvgWallThick: detailsData.avgWallThickness || "",
        Finish: "2025-Q2A", // Hardcoded
        RunnerType: costParams._overrides?.runnerType ? costParams.runnerType : (detailsData.runnerType || "N/A"),
        Regrind: detailsData.regrindPct || "",
        Cavities: costParams._overrides?.cavities ? costParams.cavities : (detailsData.noOfCavities || 0),
        Tonnage: costParams._overrides?.machineTonnage ? costParams.machineTonnage : (detailsData.machineTonnage || 0),
        // A cavity count entered in Product Information is explicit on the
        // first run. A later PlayBook Auto selection deliberately clears it.
        OverrideCavities: costParams._overrides?.cavities === true ||
          (!Object.prototype.hasOwnProperty.call(costParams._overrides || {}, "cavities") &&
            detailsData.noOfCavities !== "" && detailsData.noOfCavities != null),
        OverrideTonnage: costParams._overrides?.machineTonnage === true,
        OverrideCycleTime: costParams._overrides?.cycleTime === true,
        CycleTime: costParams._overrides?.cycleTime ? costParams.cycleTime : null,
        CreatedBy: userIdentifier,
        userEmail: user?.email || "",
        matPrice: costParams._overrides?.machineTonnage ? (costParams.matPrice || 0) : 0,
        GedbQtr: "2025-Q2A", // Hardcoded
      };

      console.log("📞 Calling stored procedure QC_SP_Plastics with params:", spParams);

      // Call the stored procedure API endpoint
      const response = await apiFetch("/api/costing/plastics", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(spParams),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Stored procedure call failed (${response.status})`);
      }

      const result = await response.json();
      console.log("✅ Stored procedure result:", result);
      return result;
    } catch (err) {
      console.error("❌ Stored procedure call failed:", err);
      throw err;
    }
  };

  const handleProgressiveQuickCost = async () => {
    if (!productData) {
      alert("Please fill in product information first");
      return;
    }

    console.log("Starting Progressive Quick Cost Analysis...");

    // Initialize progress steps immediately with descriptive labels
    const initialSteps = [
      { id: "step-1", label: "Documenting Assumptions", status: "in-progress", message: "Processing..." },
      { id: "step-2", label: "Analyzing Material Requirements", status: "pending", message: "" },
      { id: "step-3", label: "Calculating Process Costs", status: "pending", message: "" },
      { id: "step-4", label: "Computing Machine Parameters", status: "pending", message: "" },
      { id: "step-5", label: "Generating Cost Breakdown", status: "pending", message: "" },
      { id: "step-6", label: "Finalizing Analysis Report", status: "pending", message: "" },
    ];
    setProgressSteps(initialSteps);
    setCurrentProgressStep(1);
    setOverallProgress(0);
    setIsProcessing(true);
    imageRequestGenerationRef.current += 1;
    imageQuoteSnapshotRef.current = null;
    setProductImageError("");
    setProductImageUrl(null);
    setGeneratingImage(false);
    setCostData(null);
    setShowProgressTracker(true);
    setKeyInsightsData(null);
    setRiskData(null);
    setSubstituteData(null);

    try {
      // Step 1: Documenting Assumptions (16.67%)
      setProgressSteps(prev => prev.map((step, idx) => 
        idx === 0 ? { ...step, status: "in-progress", message: "Processing..." } : step
      ));
      setCurrentProgressStep(1);
      setOverallProgress(16.67);
      
      await new Promise(resolve => setTimeout(resolve, 500)); // Simulate work

      // Step 2: Analyzing Material Requirements (33.33%)
      setProgressSteps(prev => prev.map((step, idx) => 
        idx === 0 ? { ...step, status: "completed", message: "Complete" } :
        idx === 1 ? { ...step, status: "in-progress", message: "Processing..." } : step
      ));
      setCurrentProgressStep(2);
      setOverallProgress(33.33);

      await new Promise(resolve => setTimeout(resolve, 500)); // Simulate work

      // Step 3: Calculating Process Costs (50%) - Call stored procedure here
      setProgressSteps(prev => prev.map((step, idx) => 
        idx === 1 ? { ...step, status: "completed", message: "Complete" } :
        idx === 2 ? { ...step, status: "in-progress", message: "Processing..." } : step
      ));
      setCurrentProgressStep(3);
      setOverallProgress(50);

      // Call stored procedure for plastics costing
      console.log("📞 Calling stored procedure with:", { productData, playBookData });
      // A material rate is an input to SQL, never a per-part amount. SQL remains
      // responsible for runner allocation and all affected overhead components.
      let calculationPlayBook = playBookData;
      let materialPriceWarning = null;
      if (playBookData?.costParams?._overrides?.machineTonnage) {
        try {
          const response = await apiFetch("/api/mech/material-price", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              tonnage: playBookData.costParams.machineTonnage,
              manufacturingCountry: productData.manufacturingCountry,
              materialName: productData.materialSelection,
            }),
          });
          if (!response.ok) throw new Error("Material price refresh unavailable");
          const result = await response.json();
          const rawPrice = result.material_price;
          const rate = Number(rawPrice);
          if (!["number", "string"].includes(typeof rawPrice) ||
              String(rawPrice).trim() === "" || !Number.isFinite(rate) || rate <= 0) {
            throw new Error("Material price refresh returned an invalid rate");
          }
          calculationPlayBook = {
            ...playBookData,
            costParams: { ...playBookData.costParams, matPrice: rate },
          };
        } catch {
          materialPriceWarning = "Material price refresh unavailable; stored-procedure material rate retained.";
          calculationPlayBook = {
            ...playBookData,
            costParams: { ...playBookData.costParams, matPrice: 0 },
          };
        }
      }
      const spResult = await callStoredProcedure(productData, calculationPlayBook);

      // Step 4: Computing Machine Parameters (66.67%)
      setProgressSteps(prev => prev.map((step, idx) => 
        idx === 2 ? { ...step, status: "completed", message: "Complete" } :
        idx === 3 ? { ...step, status: "in-progress", message: "Processing..." } : step
      ));
      setCurrentProgressStep(4);
      setOverallProgress(66.67);

      await new Promise(resolve => setTimeout(resolve, 500)); // Simulate work

      // Step 5: Generating Cost Breakdown (83.33%)
      setProgressSteps(prev => prev.map((step, idx) => 
        idx === 3 ? { ...step, status: "completed", message: "Complete" } :
        idx === 4 ? { ...step, status: "in-progress", message: "Processing..." } : step
      ));
      setCurrentProgressStep(5);
      setOverallProgress(83.33);

      if (spResult && Array.isArray(spResult) && spResult.length > 0) {
        const resultData = spResult[0];
        const breakdown = costBreakdownFromSql(resultData);
        const { material, process, ohp, total_should_cost: total } = breakdown;
        const costParams = calculationPlayBook?.costParams || {};
        const overrides = { ...costParams._overrides };
        const baseOhpRatePercent = overheadRatePercent(ohp, material, process);
        const updatedCostParams = {
          ...costParams,
          // Display the effective SQL values; auto results are never frozen as overrides.
          // cCavities is the schema's weight-based computed reference. Cavities
          // holds the effective value actually used by SQL, including overrides.
          cavities: Number(resultData.Cavities ?? resultData.cCavities ?? 0),
          cycleTime: Number(resultData.CycleTime ?? 0),
          machineTonnage: Number(resultData.Tonnage ?? resultData.cTonnage ?? 0),
          runnerType: resultData.RunnerType || "N/A",
          ohpRate: overrides.ohpRate ? costParams.ohpRate : baseOhpRatePercent,
          _overrides: overrides,
        };
        const assumptionsArray = [];
        const dims = productData.partWeightDimensions || {};
        if (dims.partLength && dims.partWidth && dims.partHeight && dims.finishWeight) {
          assumptionsArray.push(`Part dimensions: L: ${dims.partLength}mm, W: ${dims.partWidth}mm, H: ${dims.partHeight}mm, Weight: ${dims.finishWeight}g`);
        }
        assumptionsArray.push(
          `Machine tonnage: ${updatedCostParams.machineTonnage}`,
          `Cavities: ${updatedCostParams.cavities}`,
          `Runner type: ${updatedCostParams.runnerType}`,
          `Cycle time: ${updatedCostParams.cycleTime}s`,
        );
        for (const [value, label, unit] of [
          [resultData.MHR, "Machine rate", "hour"],
          [resultData.RMPrice, "Material price", "kg"],
          [resultData.LaborCost ?? resultData.LabourCost ?? resultData.laborCost ?? resultData.labourCost, "Labour rate", "hour"],
        ]) {
          if (value !== null && value !== undefined && Number.isFinite(Number(value))) {
            assumptionsArray.push(`${label}: $${Number(value).toFixed(4)}/${unit}`);
          }
        }
        assumptionsArray.push(`OHP rate: ${Number(updatedCostParams.ohpRate).toFixed(2)}%`);
        assumptionsArray.push("Regrind percentage is recorded only; Parametric costing does not apply a regrind adjustment.");
        assumptionsArray.push("Reference rate quarter: 2025-Q2A (catalogue basis; not a current market quote).");
        if (materialPriceWarning) assumptionsArray.push(materialPriceWarning);

        let finalCostData = {
          summary: `Should cost analysis for ${resultData.PartName || "plastic part"} manufactured in ${resultData.MfrCountry || "location"}. Total cost: $${total} per unit.`,
          breakdown,
          line_items: [],
          key_insights: [],
          assumptions: assumptionsArray,
          _source: "stored_procedure",
          _originalResult: resultData,
          meta: {
            baseOhpCost: ohp,
            baseOhpRate: baseOhpRatePercent,
            currentOhpRate: updatedCostParams.ohpRate,
            materialPricePerKg: resultData.RMPrice,
            materialPriceWarning,
          },
        };
        if (overrides.ohpRate) {
          finalCostData = recalculateOHPCost(costParams.ohpRate, finalCostData, updatedCostParams);
        }
        setCostData(finalCostData);

        const mergedPlayBookState = {
          ...(playBookData || {}),
          costParams: updatedCostParams,
        };
        setPlayBookData(mergedPlayBookState);

        // Step 6: Finalizing Analysis Report (100%)
        setProgressSteps(prev => prev.map((step, idx) => 
          idx === 4 ? { ...step, status: "completed", message: "Complete" } :
          idx === 5 ? { ...step, status: "in-progress", message: "Processing..." } : step
        ));
        setCurrentProgressStep(6);
        setOverallProgress(100);

        // Generate product image, key insights, and analysis-report tabs
        await Promise.all([
          generateProductImageWithData(productData, mergedPlayBookState),
          fetchKeyInsights(productData, mergedPlayBookState),
          fetchRiskAndSubstituteData(productData, mergedPlayBookState),
        ]);

        // Mark all complete
        setProgressSteps(prev => prev.map(step => ({ ...step, status: "completed", message: "Complete" })));

        // Close dialog and navigate to results
        setShowProgressTracker(false);
        setIsProcessing(false);
        setActiveSection("results");
        setSectionStarted((prev) => ({ ...prev, playbook: true, results: true }));

        console.log("✅ Progressive Quick Cost completed successfully!");
      } else {
        throw new Error("Stored procedure returned no data");
      }
    } catch (error) {
      console.error("❌ Progressive Quick Cost failed:", error);
      const message = error?.message ||
        (typeof error?.error === "string" ? error.error : error?.error?.message) ||
        "The costing request failed. Please retry.";
      alert(`Failed to complete analysis: ${message}`);
      setIsProcessing(false);
      setShowProgressTracker(false);
    }
  };

  const handleQuickCost = async () => {
    // Use progressive tracker for better UX
    return handleProgressiveQuickCost();

  };

  const navItems = [
    {
      id: "product",
      label: "Product Information",
      icon: <DescriptionIcon />,
      disabled: false,
      title: "Build Product Profile",
      description:
        "Answer a few quick questions about your product to generate an AI-driven cost analysis with  manufacturing parameters, and detailed insights.",
    },
    // {
    //   id: "formulation",
    //   label: "Formulation",
    //   icon: <ScienceIcon />,
    //   disabled: false,
    //   title: "Ready to Build Formulation",
    //   description: "View and edit ingredient composition with functional groups and percentages.",
    // },
    {
      id: "playbook",
      label: "PlayBook",
      icon: <TuneIcon />,
      disabled: !sectionStarted.product, // Disabled until user starts product section
      title: "Ready to Configure PlayBook",
      description:
        "Configure manufacturing parameters including moisture content, process loss, packaging loss, overhead percentages, and packaging details.",
    },
    {
      id: "results",
      label: "Analysis Report",
      icon: <AssessmentIcon />,
      disabled: !sectionStarted.product, // Disabled until user starts product section
      title: "Analysis Report",
      description:
        "Access detailed cost breakdown, risk analysis, and substitute recommendations with visual charts and insights.",
    },
  ];

const costRows = useMemo(() => buildCostRows(costData?.breakdown || {}), [costData]);
 
  const costTotal = useMemo(() => {
    const declaredTotal =
      costData?.breakdown?.total_should_cost ?? costData?.breakdown?.total;
    const declaredNumber = Number(declaredTotal);
    if (Number.isFinite(declaredNumber)) return declaredNumber;
    console.log(costRows)
    return costRows.reduce((sum, row) => sum + (Number(row.value) || 0), 0);
  }, [costData, costRows]);


  return (
    <SidebarLayout>
      <Box
        sx={{
          display: "grid",
          gridTemplateRows: "auto 1fr",
          height: "100%",
          width: "100%",
          overflow: "hidden",
          bgcolor: perlColors.background,
          color: perlColors.textPrimary,
        }}
      >
        <TitleSection
          towerName="Mechanical"
          industry="Mechanical"
          commodity="Plastics Molding"
          tierImage="/images/Silver.jpg"
          tierLabel="Agentic Lite"
          showAI={false}
        />

        <Box
          sx={{
            display: "flex",
            height: "100%",
            overflow: "hidden",
          }}
        >
          <LeftNavigation
            navItems={navItems}
            activeSection={activeSection}
            onSectionChange={setActiveSection}
            showTooltips={true}
          />

          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              p: perlSpacing.mainPadding || 3,
              "&::-webkit-scrollbar": { width: "8px" },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: perlColors.border,
                borderRadius: "4px",
              },
            }}
          >
            {/* Show info notification only for product section when not started */}
            {!sectionStarted.product && activeSection === "product" && (
              <InfoNotification
                title={navItems.find((item) => item.id === activeSection)?.title}
                message={navItems.find((item) => item.id === activeSection)?.description}
                show={true}
                showButton={true}
                onButtonClick={handleStartSection}
              />
            )}

            {/* Keep CookiesQuestions mounted but hidden when not active to preserve state */}
            {sectionStarted.product && (
              <Box sx={{ display: activeSection === "product" ? "block" : "none", height:"92%"}}>
                <PlasticsQuestions
                  onComplete={handleProductComplete}
                  onDataChange={handleProductComplete}
                  onDemoModeChange={handleDemoModeChange}
                  initialData={productData}
                />
              </Box>
            )}

            {activeSection === "formulation" && sectionStarted.formulation && (
              <Box>
                <FormulationTable
                  data={recipeData}
                  onRowChange={(index, field, value) => {
                    const updated = [...recipeData];
                    updated[index][field] = value;
                    setRecipeData(updated);
                  }}
                  onRowAdd={() => {
                    setRecipeData([
                      ...recipeData,
                      { ingredient: "", percentage: 0, functional_group: null },
                    ]);
                  }}
                  onRowDelete={(index) => {
                    setRecipeData(recipeData.filter((_, i) => i !== index));
                  }}
                  functionalGroups={functionalGroups}
                  brandColor="#ff6034"
                  editable={true}
                  showTotal={true}
                />
              </Box>
            )}

            {activeSection === "playbook" && sectionStarted.playbook && (
              <Box>
                {playBookData ? (
                  <PlasticsPlayBook data={playBookData} onChange={handlePlayBookChange} />
                ) : (
                  <Typography sx={{ color: perlColors.textSecondary }}>
                    Generate recipe first to load PlayBook data
                  </Typography>
                )}
              </Box>
            )}

            {activeSection === "results" && sectionStarted.results && (
              <Box>
                {costData ? (
                  <ResultsSection
                    costData={costData}
                    costRows={costRows}
                    costTotal={costTotal}
                    riskData={riskData}
                    substituteData={substituteData}
                    productImageUrl={productImageUrl}
                    generatingImage={generatingImage}
                    imageError={productImageError}
                    onRetryImage={generateProductImage}
                    onRegenerateImage={generateProductImage}
                    productName={productData?.productName}
                    productData={productData}
                    brandColor="#ff6034"
                    assumptions={costData?.assumptions || []}
                    keyInsights={keyInsightsData}
                    substituteSectionTitle="Plastics Molding Recommendations"
                    onNavigateToPlayBook={() => {
                      setSectionStarted((prev) => ({ ...prev, playbook: true }));
                      setActiveSection("playbook");
                    }}
                    renderedAssumptions = {renderAssumptions({
                                            assumptions: costData?.assumptions || [],
                                            productData,
                                            formulaResult: costData?.formulaResult,
                                          })}
                  />
                ) : (
                  <Typography sx={{ color: perlColors.textSecondary }}>
                    Run Quick Cost to generate analysis report
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>

        {/* Quick Cost Button */}
        {sectionStarted.product &&  (
          <QuickCostButton
            onClick={handleQuickCost}
            disabled={!productData}
            loading={isProcessing}
            brandColor="#ff6034"
          />
        )}

        {/* Progressive Analysis Tracker Dialog */}
        <Dialog
          open={showProgressTracker}
          maxWidth={false}
          disableEscapeKeyDown
          PaperProps={{
            sx: {
              backgroundColor: "transparent",
              boxShadow: "none",
              borderRadius: 4,
            },
          }}
          BackdropProps={{
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.5)",
            },
          }}
        >
          <DialogContent sx={{ p: 0 }}>
            <ProgressiveAnalysisTracker
              steps={progressSteps}
              currentStepNumber={currentProgressStep}
              overallProgress={overallProgress}
            />
          </DialogContent>
        </Dialog>

        {/* Legacy Circular Loading (kept for backward compatibility) */}
        <CircularLoading
          open={isProcessing && !showProgressTracker}
          message={loadingMessage || "Running quick cost..."}
          subtext={productData?.productName ? `Product: ${productData.productName}` : undefined}
        />
      </Box>
    </SidebarLayout>
  );
}
